/**
 * 5 — Background replace: composite the cut-out foreground over a chosen backdrop.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12). Non-destructive and gated on a removal exactly as
 * before — the whole card is behind `removalVer > 0 && !locked`, so nothing new appears until one
 * has run. P3-1: these inputs recompose the live preview but never dirty the bake.
 */

import { BG_MODE_LABEL } from "../controlLabels.js";

export interface BackgroundReplaceCardProps {
  locked?: boolean;
  removalVer: number;
  setRemovalVer: React.Dispatch<React.SetStateAction<number>>;
  bgMode: "transparent" | "color" | "image";
  setBgMode: React.Dispatch<React.SetStateAction<"transparent" | "color" | "image">>;
  bgColor: string;
  setBgColor: (v: string) => void;
  blendMode: GlobalCompositeOperation;
  setBlendMode: (v: GlobalCompositeOperation) => void;
  bgImageRef: React.RefObject<HTMLImageElement | null>;
  onBgImageFile: (file: File | null | undefined) => void;
}

export function BackgroundReplaceCard(p: BackgroundReplaceCardProps) {
  const {
    locked, removalVer, setRemovalVer, bgMode, setBgMode,
    bgColor, setBgColor, blendMode, setBlendMode, bgImageRef,
    onBgImageFile,
  } = p;
  return (
    <>
        {/* 5 — Background replace — appears once the background is removed (mask). Non-destructive:
            composites the foreground over the chosen backdrop. The whole section is gated on a
            removal exactly as before, so nothing new appears until then. */}
        {/* WS-Z6 / R12: the card is an accordion BODY now. The REMOVAL GATE stays right here —
            MaskEditor asks the same question to decide whether to offer the head at all. */}
        {removalVer > 0 && !locked && (
          <div className="mask-editor__section-body" data-testid="section-bg-replace">
            <div className="mask-editor__bg-row" data-testid="bg-replace">
              <span className="mask-editor__bg-label">Background:</span>
              {(["transparent", "color", "image"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn btn--sm${bgMode === m ? " is-active" : ""}`}
                  aria-pressed={bgMode === m}
                  onClick={() => { setBgMode(m); setRemovalVer((v) => v + 1); }}
                  data-testid={`bg-mode-${m}`}
                >
                  {BG_MODE_LABEL[m]}
                </button>
              ))}
              {bgMode === "color" && (
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => { setBgColor(e.target.value); setRemovalVer((v) => v + 1); }}
                  title="Background color"
                  // F3: a swatch has no text of its own, and `title` is an advisory hint a screen
                  // reader may or may not read. Name it explicitly (`docs/design.md`, G-61).
                  aria-label="Preview backdrop color"
                  data-testid="bg-color"
                />
              )}
              {bgMode === "image" && (
                <label className="btn btn--sm mask-editor__file-btn">
                  {bgImageRef.current ? "Change image…" : "Upload image…"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="is-hidden"
                    onChange={(e) => onBgImageFile(e.target.files?.[0])}
                    data-testid="bg-image"
                  />
                </label>
              )}
              <label className="mask-editor__bg-blend">
                Blend
                <select
                  value={blendMode}
                  onChange={(e) => { setBlendMode(e.target.value as GlobalCompositeOperation); setRemovalVer((v) => v + 1); }}
                  data-testid="bg-blend"
                >
                  {(["source-over", "multiply", "screen", "overlay", "darken", "lighten"] as const).map((b) => (
                    <option key={b} value={b}>{b === "source-over" ? "normal" : b}</option>
                  ))}
                </select>
              </label>
            </div>
            {/* P3-1: say plainly that this backdrop is a PREVIEW aid. It is composited after the
                baked assets are assembled, so it never reaches the render/export — and it no
                longer dirties the bake, so changing it can't trigger a pointless re-upload.

                WS-BG: the second half of this copy tracks what the template's Background setting
                ACTUALLY paints, which is the defect F-2 was raised for. It is now BOTH paths:
                Transparent empties the canvas, and Solid color / Brand color paint their stored
                colour, on the mask render AND on the baked mock-up
                (`MockupInputs::background_color`). The one real limit left is the container:
                JPG has no alpha, so a Transparent template exported as JPG is flattened onto
                white. `mask-editor-round-trip.test.tsx` pins this copy. */}
            <p className="faint mask-editor__hint" data-testid="bg-preview-only">
              Preview only — this backdrop is not saved and is not what the render/export uses. The
              template’s <strong>Background</strong> setting is what the export paints:
              <strong> Solid color</strong> and <strong>Brand color</strong> paint their colour on
              both the mask render and the baked mock-up, and <strong>Transparent</strong> empties
              the canvas — except in JPG, which has no transparency and is flattened onto white.
            </p>
          </div>
        )}
    </>
  );
}
